// Single source of truth for every amount computed in the bulk purchase-return
// flow (side panel batch rows, table rows, footer totals, save payload).
// A batch's `qty`, `purchase_rate` and `discount_amount` (flat ₹) are the only
// user-editable inputs; everything else here is derived from them.

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const round = (value) => Math.round(toNumber(value) * 100) / 100;

// qty + rate + flat discount + uom -> taxable/tax/total for one batch line.
// `base_quantity` is the selected uom's pack size (e.g. "Box of 10" = 10),
// used to convert the entered qty into base units before pricing it.
export const calculateBatchAllocation = ({
  purchase_rate = 0,
  qty = 0,
  base_quantity = 1,
  discount_amount = 0,
  cgst_percentage = 0,
  sgst_percentage = 0,
  igst_percentage = 0,
  is_inter_state = false,
} = {}) => {
  const rate = toNumber(purchase_rate);
  const quantity = toNumber(qty);
  const baseQty = toNumber(base_quantity) || 1;

  const effective_units = quantity * baseQty;
  const base_amount = round(rate * effective_units);

  // Flat discount can never exceed the base amount it's discounting.
  const clampedDiscount = Math.min(Math.max(toNumber(discount_amount), 0), base_amount);
  const taxable_amount = round(base_amount - clampedDiscount);

  const cgst_amount = is_inter_state
    ? 0
    : round((toNumber(cgst_percentage) / 100) * taxable_amount);
  const sgst_amount = is_inter_state
    ? 0
    : round((toNumber(sgst_percentage) / 100) * taxable_amount);
  const igst_amount = is_inter_state
    ? round((toNumber(igst_percentage) / 100) * taxable_amount)
    : 0;
  const tax_amount = round(cgst_amount + sgst_amount + igst_amount);
  const tax_percentage = is_inter_state
    ? toNumber(igst_percentage)
    : toNumber(cgst_percentage) + toNumber(sgst_percentage);

  const total_amount = round(taxable_amount + tax_amount);

  return {
    effective_units,
    base_amount,
    discount_amount: round(clampedDiscount),
    taxable_amount,
    tax_percentage: round(tax_percentage),
    cgst_amount,
    sgst_amount,
    igst_amount,
    tax_amount,
    total_amount,
  };
};

// Rolls up an item's allocated batches (qty > 0) into item-row totals.
export const sumBatchesForItem = (batches = []) => {
  const totals = batches
    .filter((batch) => toNumber(batch.qty) > 0)
    .reduce(
      (acc, batch) => {
        acc.qty += toNumber(batch.qty);
        acc.base_amount += toNumber(batch.base_amount);
        acc.discount_amount += toNumber(batch.discount_amount);
        acc.taxable_amount += toNumber(batch.taxable_amount);
        acc.tax_amount += toNumber(batch.tax_amount);
        acc.cgst_amount += toNumber(batch.cgst_amount);
        acc.sgst_amount += toNumber(batch.sgst_amount);
        acc.igst_amount += toNumber(batch.igst_amount);
        acc.total_amount += toNumber(batch.total_amount);
        return acc;
      },
      {
        qty: 0,
        base_amount: 0,
        discount_amount: 0,
        taxable_amount: 0,
        tax_amount: 0,
        cgst_amount: 0,
        sgst_amount: 0,
        igst_amount: 0,
        total_amount: 0,
      }
    );

  return Object.fromEntries(
    Object.entries(totals).map(([key, value]) => [key, round(value)])
  );
};

// Required / allocated / remaining qty + amounts for one item row, derived
// live from its batches — used by both the table's progress column and the
// allocate side panel's stats bar, so they can never drift apart.
export const getRowAllocationSummary = (row) => {
  const batches = row?.batches || [];
  const required = round(
    batches.reduce((sum, batch) => sum + toNumber(batch.total_allocatable_qty), 0)
  );
  const rowTotals = sumBatchesForItem(batches);
  const remaining = round(Math.max(required - rowTotals.qty, 0));

  let status = "not_allocated";
  if (rowTotals.qty > 0 && rowTotals.qty < required) status = "partial";
  else if (required > 0 && rowTotals.qty >= required) status = "full";

  return { required, remaining, status, ...rowTotals };
};

// Rolls up all item rows into the document-level totals used by the footer.
export const sumTotalsFromRows = (rows = []) => {
  const totals = rows.reduce(
    (acc, row) => {
      const rowTotals = sumBatchesForItem(row.batches || []);
      acc.allocatedQty += rowTotals.qty;
      acc.amount += rowTotals.total_amount;
      acc.discount += rowTotals.discount_amount;
      acc.tax += rowTotals.tax_amount;
      return acc;
    },
    { allocatedQty: 0, amount: 0, discount: 0, tax: 0 }
  );

  return {
    itemsCount: rows.length,
    allocatedQty: round(totals.allocatedQty),
    amount: round(totals.amount),
    totalDiscount: round(totals.discount),
    totalTax: round(totals.tax),
    subTotal: round(totals.amount - totals.tax),
  };
};

// Groups every row's allocated batches (qty > 0) by customer (cmr_id +
// ship-to + pay-to address) into the final save payload.
export const buildCmrAllocationPayload = (rows = []) => {
  const groups = new Map();

  rows.forEach((row) => {
    (row.batches || [])
      .filter((batch) => toNumber(batch.qty) > 0)
      .forEach((batch) => {
        const shipId = batch.cmr_ship_to_address?.cmr_ship_address_id || "";
        const payId = batch.cmr_pay_to_address?.cmr_pay_address_id || "";
        const key = `${batch.cmr_id}__${shipId}__${payId}`;

        if (!groups.has(key)) {
          groups.set(key, {
            cmr_id: batch.cmr_id,
            cmr_code: batch.cmr_code,
            cmr_name: batch.cmr_name,
            cmr_gstin: batch.cmr_gstin,
            cmr_ship_to_address: batch.cmr_ship_to_address,
            cmr_pay_to_address: batch.cmr_pay_to_address,
            items: [],
          });
        }

        groups.get(key).items.push({
          item_id: row.item_id,
          item_code: row.item_code,
          item_name: row.item_name,
          item_hsn: row.item_hsn,
          item_uom: batch.item_uom,
          item_uom_id: batch.item_uom_id,
          item_batch_number: batch.item_batch_number,
          item_exp_date: batch.item_exp_date,
          to_bin_location: batch.to_bin_location,
          item_qty: batch.qty,
          item_batch_purchase_rate: batch.purchase_rate,
          item_batch_discount_amount: batch.discount_amount,
          item_batch_tax_amount: batch.tax_amount,
          item_batch_cgst_amount: batch.cgst_amount,
          item_batch_sgst_amount: batch.sgst_amount,
          item_batch_igst_amount: batch.igst_amount,
          item_batch_total_amount: batch.total_amount,
        });
      });
  });

  return Array.from(groups.values());
};

// Standard GST rule (matches address-bar-sales.js's onSave, which is the
// only other place in the app that derives this rather than trusting a
// passed-through API value): same state as the store = intra-state =
// CGST+SGST; different state = inter-state = IGST. Batches can carry an
// is_inter_state from wherever their vendor/address came from, but that can
// go stale the moment "Change Vendor"/"Change Address" swaps the ship-to
// address out from under it — this recomputes it fresh from the current
// address so the submitted tax split always matches the address actually
// being shipped to.
export const resolveIsInterState = (storeState, shipToAddress) => {
  const normalizedStore = (storeState || "").toLowerCase().trim();
  const normalizedShip = (shipToAddress?.cmr_ship_address_state || "")
    .toLowerCase()
    .trim();
  if (!normalizedStore || !normalizedShip) return false;
  return normalizedStore !== normalizedShip;
};

// Builds one /purchaseorder/return/add-shaped {orderData, itemData,
// itemBatchData} document per customer group (same cmr_id + ship-to +
// pay-to grouping as buildCmrAllocationPayload), matching the field
// conventions of the existing single purchase-return creation flow
// (create-edit-view-pur-return.js) so a bulk return reads the same way to
// the backend as N individually-created ones would.
export const buildPurchaseReturnDocuments = (
  rows = [],
  { storeId, storeState, documentDate } = {}
) => {
  const groups = new Map();

  rows.forEach((row) => {
    (row.batches || [])
      .filter((batch) => toNumber(batch.qty) > 0)
      .forEach((batch) => {
        const shipId = batch.cmr_ship_to_address?.cmr_ship_address_id || "";
        const payId = batch.cmr_pay_to_address?.cmr_pay_address_id || "";
        const key = `${batch.cmr_id}__${shipId}__${payId}`;

        if (!groups.has(key)) {
          groups.set(key, {
            cmr_id: batch.cmr_id,
            cmr_code: batch.cmr_code,
            cmr_name: batch.cmr_name,
            cmr_gstin: batch.cmr_gstin,
            cmr_ship_to_address: batch.cmr_ship_to_address,
            cmr_pay_to_address: batch.cmr_pay_to_address,
            is_inter_state: resolveIsInterState(storeState, batch.cmr_ship_to_address),
            itemsByItemId: new Map(),
          });
        }

        const group = groups.get(key);
        // Re-derive this batch's tax split under the group's own
        // (store-vs-ship-to) is_inter_state. The batch's cgst/sgst/igst
        // percentages were populated assuming whatever is_inter_state the
        // vendor lookup originally had — if that flips here (e.g. the
        // vendor's data assumed intra-state so only cgst/sgst were ever
        // set, but this ship-to address is actually out of state), reusing
        // those fields as-is would silently zero the tax out, since the
        // now-relevant igst_percentage was never populated. Normalize to
        // the batch's total GST% first, then split it into whichever
        // bucket the corrected is_inter_state actually calls for.
        const totalGstPercentage =
          toNumber(batch.cgst_percentage) + toNumber(batch.sgst_percentage) ||
          toNumber(batch.igst_percentage);
        const recalculated = calculateBatchAllocation({
          ...batch,
          is_inter_state: group.is_inter_state,
          cgst_percentage: group.is_inter_state ? 0 : totalGstPercentage / 2,
          sgst_percentage: group.is_inter_state ? 0 : totalGstPercentage / 2,
          igst_percentage: group.is_inter_state ? totalGstPercentage : 0,
        });

        if (!group.itemsByItemId.has(row.item_id)) {
          group.itemsByItemId.set(row.item_id, {
            item_id: row.item_id,
            item_code: row.item_code,
            item_name: row.item_name,
            item_generic_name: row.item_generic_name || "",
            item_hsn: row.item_hsn,
            item_uom: row.item_uom,
            item_uom_id: batch.item_uom_id,
            item_manufacturer_id: row.item_manufacturer_id || "",
            item_manufacturer_name: row.item_manufacturer_name || "",
            lines: [],
          });
        }
        group.itemsByItemId.get(row.item_id).lines.push({ batch, recalculated, totalGstPercentage });
      });
  });

  return Array.from(groups.values()).map((group) => {
    const items = Array.from(group.itemsByItemId.values());

    const itemData = items.map((item) => {
      const totals = item.lines.reduce(
        (acc, { batch, recalculated }) => {
          acc.qty += toNumber(batch.qty);
          acc.pack_size += toNumber(batch.qty) * toNumber(batch.base_quantity);
          acc.total_amount += recalculated.total_amount;
          acc.tax_amount += recalculated.tax_amount;
          acc.discount_amount += recalculated.discount_amount;
          acc.cgst_amount += recalculated.cgst_amount;
          acc.sgst_amount += recalculated.sgst_amount;
          acc.igst_amount += recalculated.igst_amount;
          acc.tax_percentage += recalculated.tax_percentage;
          return acc;
        },
        {
          qty: 0,
          pack_size: 0,
          total_amount: 0,
          tax_amount: 0,
          discount_amount: 0,
          cgst_amount: 0,
          sgst_amount: 0,
          igst_amount: 0,
          tax_percentage: 0,
        }
      );
      const avgTaxPercentage = item.lines.length
        ? totals.tax_percentage / item.lines.length
        : 0;

      return {
        item_code: item.item_code,
        item_id: item.item_id,
        item_generic_name: item.item_generic_name,
        item_name: item.item_name,
        item_hsn: item.item_hsn,
        item_uom: item.item_uom,
        item_quantity: round(totals.qty),
        item_pack_size: round(totals.pack_size),
        item_free_quantity: 0,
        item_total_amount: round(totals.total_amount),
        item_tax_amount: round(totals.tax_amount),
        item_discount_amount: round(totals.discount_amount),
        item_gst: round(avgTaxPercentage),
        item_cgst: group.is_inter_state ? 0 : round(totals.cgst_amount),
        item_sgst: group.is_inter_state ? 0 : round(totals.sgst_amount),
        item_igst: group.is_inter_state ? round(totals.igst_amount) : 0,
        gort_id: null,
        base_ref_id: null,
        base_ref_type: null,
        base_ref_line_id: null,
        base_ref_doc_number: null,
        item_uom_id: item.item_uom_id,
        item_manufacturer_id: item.item_manufacturer_id,
        item_manufacturer_name: item.item_manufacturer_name,
      };
    });

    const itemBatchData = items.flatMap((item) =>
      item.lines.map(({ batch, recalculated, totalGstPercentage }) => {
        const qty = toNumber(batch.qty);
        return {
          item_batch_number: batch.item_batch_number,
          item_sellable_quantity: qty,
          item_batch_free_quantity: 0,
          item_batch_total_effective_qauntity: qty,
          item_batch_purchase_rate: toNumber(batch.purchase_rate),
          item_batch_total_base_purchase_rate: recalculated.base_amount,
          item_batch_for_qauntity: 0,
          item_batch_for_free_qauntity: 0,
          item_batch_scheme_discount_percentage: 0,
          item_batch_scheme_discount_amount: 0,
          item_batch_scheme_purchase_rate: recalculated.base_amount,
          item_batch_discount_percentage: 0,
          item_batch_gen_discount_amount: recalculated.discount_amount,
          item_batch_discounted_purchase_rate: recalculated.taxable_amount,
          item_batch_margin_percentage: 0,
          item_batch_discount_amount: recalculated.discount_amount,
          item_batch_tax_percentage: recalculated.tax_percentage,
          item_batch_tax_amount: recalculated.tax_amount,
          item_batch_final_purchase_rate: qty > 0 ? round(recalculated.total_amount / qty) : 0,
          item_batch_total_purchase_rate: recalculated.total_amount,
          item_batch_unit_price: toNumber(batch.unit_price),
          item_exp_date: batch.item_exp_date,
          item_id: item.item_id,
          to_bin_location: batch.to_bin_location,
          to_bin_id: batch.to_bin_id || null,
          item_batch_cgst_percentage: group.is_inter_state ? 0 : totalGstPercentage / 2,
          item_batch_sgst_percentage: group.is_inter_state ? 0 : totalGstPercentage / 2,
          item_batch_igst_percentage: group.is_inter_state ? totalGstPercentage : 0,
          item_batch_cgst_amount: group.is_inter_state ? 0 : recalculated.cgst_amount,
          item_batch_sgst_amount: group.is_inter_state ? 0 : recalculated.sgst_amount,
          item_batch_igst_amount: group.is_inter_state ? recalculated.igst_amount : 0,
        };
      })
    );

    const orderTotals = itemData.reduce(
      (acc, item) => {
        acc.tax += toNumber(item.item_tax_amount);
        acc.discount += toNumber(item.item_discount_amount);
        acc.total += toNumber(item.item_total_amount);
        return acc;
      },
      { tax: 0, discount: 0, total: 0 }
    );

    return {
      orderData: {
        is_draft_order: true,
        store_id: storeId || "",
        prt_total_gst: round(orderTotals.tax),
        prt_total_discount: round(orderTotals.discount),
        prt_payment_status: "paid",
        prt_transaction_id: "",
        prt_billing_address: "",
        prt_sub_total: round(orderTotals.total - orderTotals.tax),
        prt_total_amount: round(orderTotals.total),
        cmr_code: group.cmr_code,
        cmr_id: group.cmr_id,
        cmr_name: group.cmr_name,
        remarks: "",
        prt_document_date: documentDate,
        prt_delivery_date: documentDate,
        prt_reference_inv_date: documentDate,
        cmr_gstin: group.cmr_gstin || null,
        prt_round_off_value: 0,
        prt_tcs_or_tds_percentage_id: "",
        prt_tcs_or_tds_percentage: "0.00",
        prt_tds_tcs_type: "",
        prt_tcs_or_tds_amount: "0.00",
        prt_is_invoice_dis_percentage: true,
        prt_invoice_discount: "0.00",
        prt_invoice_discount_percentage: "0.00",
        prt_pay_to_address: group.cmr_pay_to_address || null,
        prt_ship_to_address: group.cmr_ship_to_address || null,
        gst_type: group.is_inter_state ? "Inter" : "Intra",
        customer_invoice_number: null,
      },
      itemData,
      itemBatchData,
    };
  });
};

export { round, toNumber };
